import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import ControlPanelLayout from "./ControlPanelLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, Play, Pencil, Trash2, Loader2, ChevronLeft, ChevronRight, CalendarClock, Link2 } from "lucide-react";
import { toast } from "sonner";

interface Meeting {
  id: string;
  title: string;
  description: string | null;
  meeting_code: string;
  scheduled_start: string | null;
  scheduled_end: string | null;
  is_active: boolean;
  created_at: string;
}

const PAGE_SIZE = 10;

function makeCode() {
  const seg = () =>
    Math.random().toString(36).replace(/[^a-z0-9]/g, "").slice(0, 4).toUpperCase().padEnd(4, "X");
  return `${seg()}-${seg()}-${seg()}`;
}

export default function CPMeetings() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Meeting | null>(null);
  const [form, setForm] = useState({ title: "", description: "", date: "", time: "", duration: 60 });

  const load = async () => {
    if (!user) return;
    setLoading(true);
    const { data, error } = await supabase
      .from("meetings")
      .select("id, title, description, meeting_code, scheduled_start, scheduled_end, is_active, created_at")
      .eq("host_id", user.id)
      .order("scheduled_start", { ascending: false, nullsFirst: false });
    if (error) toast.error(error.message);
    setMeetings(data || []);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, [user]);

  const openNew = () => {
    setEditing(null);
    const now = new Date();
    setForm({
      title: "",
      description: "",
      date: now.toISOString().slice(0, 10),
      time: now.toTimeString().slice(0, 5),
      duration: 60,
    });
    setOpen(true);
  };

  const openEdit = (m: Meeting) => {
    setEditing(m);
    const start = m.scheduled_start ? new Date(m.scheduled_start) : new Date();
    const end = m.scheduled_end ? new Date(m.scheduled_end) : new Date(start.getTime() + 3600000);
    const dur = Math.max(15, Math.round((end.getTime() - start.getTime()) / 60000));
    setForm({
      title: m.title,
      description: m.description || "",
      date: start.toISOString().slice(0, 10),
      time: start.toTimeString().slice(0, 5),
      duration: dur,
    });
    setOpen(true);
  };

  const save = async () => {
    if (!user || !form.title.trim()) return toast.error("Title is required");
    const start = new Date(`${form.date}T${form.time}:00`);
    if (Number.isNaN(start.getTime())) return toast.error("Invalid date/time");
    const end = new Date(start.getTime() + form.duration * 60000);

    if (editing) {
      const { error } = await supabase
        .from("meetings")
        .update({
          title: form.title.trim(),
          description: form.description.trim() || null,
          scheduled_start: start.toISOString(),
          scheduled_end: end.toISOString(),
        })
        .eq("id", editing.id);
      if (error) return toast.error(error.message);
      toast.success("Meeting updated");
    } else {
      const { error } = await supabase.from("meetings").insert({
        host_id: user.id,
        title: form.title.trim(),
        description: form.description.trim() || null,
        meeting_code: makeCode(),
        scheduled_start: start.toISOString(),
        scheduled_end: end.toISOString(),
        is_active: false,
      });
      if (error) return toast.error(error.message);
      toast.success("Meeting created");
    }
    setOpen(false);
    load();
  };

  const remove = async (id: string) => {
    if (!confirm("Delete this meeting?")) return;
    const { error } = await supabase.from("meetings").delete().eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("Deleted");
    load();
  };

  const start = async (m: Meeting) => {
    if (!m.is_active) {
      await supabase.from("meetings").update({ is_active: true }).eq("id", m.id);
    }
    navigate(`/meeting/${m.id}`);
  };

  const copyInvite = (meetingCode: string) => {
    navigator.clipboard.writeText(`${window.location.origin}/join/${meetingCode}`);
    toast.success("Join link copied");
  };

  const now = Date.now();
  const status = (m: Meeting): "Live" | "Upcoming" | "Completed" => {
    if (m.is_active) return "Live";
    if (m.scheduled_start && new Date(m.scheduled_start).getTime() > now) return "Upcoming";
    return "Completed";
  };

  const totals = useMemo(
    () => ({
      total: meetings.length,
      live: meetings.filter((m) => m.is_active).length,
      upcoming: meetings.filter((m) => !m.is_active && m.scheduled_start && new Date(m.scheduled_start).getTime() > now).length,
    }),
    [meetings, now]
  );

  const totalPages = Math.max(1, Math.ceil(meetings.length / PAGE_SIZE));
  const pageItems = meetings.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <ControlPanelLayout>
      <div className="space-y-6">
        <section className="rounded-3xl border border-border/60 bg-[linear-gradient(135deg,hsl(var(--background)),hsl(var(--primary)/0.08))] p-6 shadow-sm">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl">
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Hosted meetings</p>
              <h2 className="mt-2 text-3xl font-semibold tracking-tight">Plan, edit, and launch meetings without leaving the workspace.</h2>
              <p className="mt-3 text-muted-foreground">
                This view keeps the host workflow simple: create the meeting, copy the guest link, and start from the correct room when it is time.
              </p>
            </div>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button onClick={openNew}>
                  <Plus className="h-4 w-4 mr-2" /> New meeting
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{editing ? "Edit meeting" : "Create meeting"}</DialogTitle>
                  <DialogDescription>
                    {editing ? "Update meeting details." : "Schedule a new meeting."}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label>Title</Label>
                    <Input
                      value={form.title}
                      onChange={(e) => setForm({ ...form, title: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Textarea
                      value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Date</Label>
                      <Input
                        type="date"
                        value={form.date}
                        onChange={(e) => setForm({ ...form, date: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Time</Label>
                      <Input
                        type="time"
                        value={form.time}
                        onChange={(e) => setForm({ ...form, time: e.target.value })}
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Duration (minutes)</Label>
                    <Input
                      type="number"
                      min={15}
                      step={15}
                      value={form.duration}
                      onChange={(e) =>
                        setForm({ ...form, duration: parseInt(e.target.value) || 60 })
                      }
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={save}>{editing ? "Save" : "Create"}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </section>

        <div className="grid gap-4 md:grid-cols-3">
          <SummaryCard label="Total meetings" value={String(totals.total)} helper="Everything you've hosted" />
          <SummaryCard label="Live now" value={String(totals.live)} helper="Currently active rooms" />
          <SummaryCard label="Upcoming" value={String(totals.upcoming)} helper="Scheduled in the future" />
        </div>

        <Card className="border-border/60 shadow-sm">
          <CardHeader className="flex flex-col gap-3 border-b border-border/60 pb-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-lg">
                <CalendarClock className="h-5 w-5 text-primary" />
                Meeting library
              </CardTitle>
              <CardDescription>Review status, update plans, and launch the correct meeting room.</CardDescription>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : meetings.length === 0 ? (
              <div className="py-12 text-center text-muted-foreground">
                No meetings yet. Click <strong>New meeting</strong>.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Meeting</TableHead>
                    <TableHead className="hidden md:table-cell">Schedule</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pageItems.map((m) => {
                    const meetingStatus = status(m);
                    return (
                      <TableRow key={m.id}>
                        <TableCell>
                          <div className="font-medium">{m.title}</div>
                          <div className="mt-1 text-xs text-muted-foreground font-mono">{m.meeting_code}</div>
                          {m.description && <div className="mt-1 text-xs text-muted-foreground line-clamp-2">{m.description}</div>}
                        </TableCell>
                        <TableCell className="hidden md:table-cell text-sm">
                          {m.scheduled_start ? new Date(m.scheduled_start).toLocaleString() : "-"}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              meetingStatus === "Live"
                                ? "default"
                                : meetingStatus === "Upcoming"
                                ? "secondary"
                                : "outline"
                            }
                          >
                            {meetingStatus}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button size="sm" onClick={() => start(m)}>
                              <Play className="h-3 w-3 mr-1" /> Start
                            </Button>
                            <Button size="icon" variant="ghost" onClick={() => copyInvite(m.meeting_code)}>
                              <Link2 className="h-4 w-4" />
                            </Button>
                            <Button size="icon" variant="ghost" onClick={() => openEdit(m)}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button size="icon" variant="ghost" onClick={() => remove(m.id)}>
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {meetings.length > PAGE_SIZE && (
          <div className="flex items-center justify-end gap-2 text-sm">
            <span className="text-muted-foreground">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="icon"
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              disabled={page === totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    </ControlPanelLayout>
  );
}

function SummaryCard({ label, value, helper }: { label: string; value: string; helper: string }) {
  return (
    <Card className="border-border/60 shadow-sm">
      <CardContent className="p-5">
        <p className="text-sm font-medium text-muted-foreground">{label}</p>
        <p className="mt-2 text-3xl font-semibold tracking-tight">{value}</p>
        <p className="mt-1 text-xs text-muted-foreground">{helper}</p>
      </CardContent>
    </Card>
  );
}
